import { IconNames } from '@blueprintjs/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { formatDate } from '@strata/helper-functions';
import { FormSection } from '@strata/react-form-section';
import { type ColumnDef, type ColumnDefGeneric, Grid, SecondaryData } from '@strata/react-grid';
import { MessagingBlock } from '@strata/react-messaging-block';
import { useParams } from 'react-router';

import { type ReferralAuth, useGetReferralAuthsQuery } from 'store/api/clientApi';
import { type ClaimDetails } from 'store/api/medbillingApi';
import { useGetPrescriberDetailsQuery, useGetSupplierDetailsQuery } from 'store/api/referenceApi';

const initialSortProperties = [{ id: 'authStart', desc: true }];

/* eslint-disable react/prop-types */
const NPICell: ColumnDefGeneric<
    ReferralAuth,
    'billingProvider' | 'servicingProvider' | 'requestingProvider'
>['cell'] = ({ getValue }) => {
    const npiValue = getValue()?.trim();
    const { data: supplierDetails } = useGetSupplierDetailsQuery(
        npiValue ? { supplierNpi: npiValue } : skipToken,
    );
    const { data: prescriberDetails } = useGetPrescriberDetailsQuery(
        npiValue && !supplierDetails?.organizationName ? { prescriberNpi: npiValue } : skipToken,
    );

    const getDisplayText = (npi?: string) => {
        if (supplierDetails?.organizationName) {
            return `${supplierDetails.organizationName} (${npi})`;
        }
        if (prescriberDetails?.prescriberName) {
            return `${prescriberDetails.prescriberName} (${npi})`;
        }
        return npi;
    };

    return <SecondaryData data={getDisplayText(npiValue)} />;
};
/* eslint-enable react/prop-types */

const columns: ColumnDef<ReferralAuth>[] = [
    {
        accessorKey: 'authStart',
        header: 'Auth Start',
        size: 110,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        accessorKey: 'authExpiration',
        header: 'Auth End',
        size: 110,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        accessorKey: 'caseId',
        header: 'Auth ID',
        size: 150,
        enableSorting: false,
    },
    {
        accessorKey: 'requestingProvider',
        header: 'Requesting Provider',
        size: 150,
        enableSorting: false,
        cell: NPICell,
    },
    {
        accessorKey: 'servicingFacility',
        header: 'Servicing Facility',
        size: 150,
        enableSorting: false,
        cell: NPICell,
    },
    {
        accessorKey: 'servicingProvider',
        header: 'Servicing Provider',
        size: 150,
        enableSorting: false,
        cell: NPICell,
    },
    {
        accessorKey: 'billingProvider',
        header: 'Billing Provider',
        size: 150,
        enableSorting: false,
        cell: NPICell,
    },
];

interface AuthDetailsProps {
    claimDetails?: ClaimDetails;
    claimDetailsLoading?: boolean;
}

export const AuthDetails = ({
    claimDetails = undefined,
    claimDetailsLoading = true,
}: AuthDetailsProps) => {
    const { payerId } = useParams() as { payerId: string };
    const { data: auths, isFetching: authsLoading } = useGetReferralAuthsQuery(
        claimDetails?.referralId ? { payerId, referralId: claimDetails.referralId } : skipToken,
    );
    return (
        <FormSection
            sectionTitle="Auth Details"
            isLoading={claimDetailsLoading || authsLoading}
            content={
                <Grid
                    data={auths}
                    sortProperties={initialSortProperties}
                    columns={columns}
                    noResults={
                        <div className="padding__around--large">
                            <MessagingBlock
                                size={MessagingBlock.Sizes.SMALL}
                                icon={IconNames.DOCUMENT}
                                primaryText="No Authorizations available"
                            />
                        </div>
                    }
                />
            }
        />
    );
};
